import api from 'api'
import { AuthResponse } from 'models/response'
import { startRegistration, startAuthentication, browserSupportsWebAuthn } from '@simplewebauthn/browser'

/**
 * Builds Axios config that sends the Turnstile token via BOTH the header
 * and a `turnstileToken` field merged into the request body.
 *
 * Why both? Cloudflare's edge proxy can strip `cf-*` headers before they
 * reach the origin, especially on Safari mobile. The backend already has a
 * fallback that reads `turnstileToken` from the JSON body when the header
 * is missing (see RequireTurnstileAttribute.GetTurnstileTokenAsync).
 */
const turnstileConfig = (turnstileToken?: string) =>
	turnstileToken ? { headers: { 'cf-turnstile-response': turnstileToken }, _turnstileToken: turnstileToken } : {}

/**
 * Wraps api.post to merge the turnstile token into the request body when present.
 */
const postWithTurnstile = <T>(url: string, data: any, turnstileToken?: string) => {
	const config = turnstileConfig(turnstileToken)
	const body = config._turnstileToken ? { ...data, turnstileToken: config._turnstileToken } : data
	const { _turnstileToken: _, ...axiosConfig } = config
	return api.post<T>(url, body, axiosConfig)
}

export const setToken = (token: any): void => {
	if (token) {
		localStorage.setItem('token', token)
	} else {
		localStorage.removeItem('token')
	}
}

export const getToken = (): string | any => {
	return JSON.parse(localStorage.getItem('token') as string)
}

export const setRefreshToken = (token: any): void => {
	if (token) {
		localStorage.setItem('refreshToken', token)
	} else {
		localStorage.removeItem('refreshToken')
	}
}

export const getRefreshToken = (): string | any => {
	return localStorage.getItem('refreshToken')
}

export const passwordRegister = (data: any, turnstileToken?: string) => {
	return postWithTurnstile<AuthResponse>('/auth/password/register', data, turnstileToken)
}

export const passwordResendRegisterEmail = (data: any) => {
	return api.post<AuthResponse>('/auth/password/resend-register-email', data)
}

export const passwordSignIn = (data: any, turnstileToken?: string) => {
	return postWithTurnstile<AuthResponse>('/auth/password/sign-in', data, turnstileToken)
}

export const passwordImSignIn = (data: any) => {
	return api.post<AuthResponse>('/auth/password/im-sign-in', data)
}

export const phoneStart = (data: any) => {
	return api.post<AuthResponse>('/auth/phone/start', data)
}

export const phoneVerify = (data: any) => {
	return api.post<AuthResponse>('/auth/phone/verify', data)
}

export const verifyOtp = (data: any) => {
	return api.post<AuthResponse>('/auth/password/verify-otp', data)
}

export const resendOtp = (data: any) => {
	return api.post<AuthResponse>('/auth/password/resend-otp', data)
}

export const verifyEmailToken = (data: any) => {
	return api.post<AuthResponse>('/auth/password/verify-email-token', data)
}

export const validateToken = (data: any) => {
	const { token } = data
	return api.post<any>(`/auth/validate-token?token=${token}`)
}

export const checkEmailAvailability = (data: any) => {
	return api.post<any>('/auth/password/check-email-availability', data)
}

export const confirmEmailChange = (data: any) => {
	return api.post<AuthResponse>('/profile/email-change/confirm', data)
}

export const startUpdatePassword = () => {
	return api.post<any>('/auth/password/start-update-password')
}

export const updatePassword = (data: any) => {
	return api.post<any>('/auth/password/update-password', data)
}

export const logout = () => {
	return api.post('/auth/sign-out')
}

export const refresh = (refreshToken: string) => {
	return api.post<AuthResponse>('/auth/refresh', { refreshToken })
}

export const passwordForget = (data: any, turnstileToken?: string) => {
	return postWithTurnstile<any>('/auth/password', data, turnstileToken)
}

export const passwordRecover = (data: any, turnstileToken?: string) => {
	return postWithTurnstile<any>('/auth/password/recover', data, turnstileToken)
}

// Basic API endpoints for passkey
export const passkeyRegisterOptions = (data: any) => {
	return api.post<AuthResponse>('/auth/passkey/register/options', data)
}

export const passkeyRegister = (data: any) => {
	return api.post<AuthResponse>('/auth/passkey/register', data)
}

export const passkeyLoginOptions = (data: any) => {
	return api.post<AuthResponse>('/auth/passkey/login/options', data)
}

export const passkeyLogin = (data: any) => {
	return api.post<AuthResponse>('/auth/passkey/login', data)
}

// Passkey registration flow using @simplewebauthn/browser
export const registerPasskey = async ({
	userName,
	displayName,
}: {
	userName: string
	displayName: string
}): Promise<any> => {
	try {
		// Step 1: Get registration options from server
		const optionsResponse = await passkeyRegisterOptions({ userName, displayName })

		// Extract options and token from response
		const { options, token } = optionsResponse as any

		// Step 2: Use @simplewebauthn/browser to create credential
		// It automatically handles all base64url encoding/decoding
		const credential = await startRegistration(options)

		// Step 3: Convert credential response to format expected by server
		const registrationData = {
			attestationResponse: {
				id: credential.id,
				rawId: credential.rawId,
				type: 0,
				extensions: credential.clientExtensionResults,
				response: {
					attestationObject: credential.response.attestationObject,
					clientDataJSON: credential.response.clientDataJSON,
				},
			},
			token,
		}

		// Step 4: Send registration data to server
		const registrationResponse = await passkeyRegister(registrationData)
		return registrationResponse
	} catch (error: any) {
		throw new Error(`Passkey registration failed: ${error.message}`)
	}
}

// Passkey login flow using @simplewebauthn/browser
export const loginPasskey = async (): Promise<any> => {
	try {
		// Step 1: Get authentication options from server without username
		const optionsResponse = await passkeyLoginOptions({})

		// Check if response has options structure like registration
		const { options, token } = optionsResponse as any

		// Step 2: Use @simplewebauthn/browser to authenticate
		// It automatically handles all base64url encoding/decoding
		const credential = await startAuthentication(options)

		// Step 3: Convert credential response to format expected by server
		const authenticationData = {
			assertionResponse: {
				id: credential.id,
				rawId: credential.rawId,
				type: 0,
				extensions: credential.clientExtensionResults,
				response: {
					authenticatorData: credential.response.authenticatorData,
					clientDataJSON: credential.response.clientDataJSON,
					signature: credential.response.signature,
					userHandle: credential.response.userHandle || null,
				},
			},
			token,
		}

		// Step 4: Send authentication data to server
		const loginResponse = await passkeyLogin(authenticationData)
		return loginResponse
	} catch (error: any) {
		const errorMsg = error.message || error.data || ''
		const friendlyMessage = errorMsg.includes('The operation either timed out or was not allowed')
			? 'Passkey Login unsuccessful. The process was interrupted because it timed out or was rejected. Please try again.'
			: `Passkey Login unsuccessful: ${errorMsg}`

		throw new Error(friendlyMessage)
	}
}

// Check if WebAuthn is supported using @simplewebauthn/browser (SSR safe)
export const isPasskeySupported = (): boolean => {
	// Ensure we're in a browser environment
	if (typeof window === 'undefined' || typeof navigator === 'undefined') {
		return false
	}

	// Use helper from @simplewebauthn/browser
	return browserSupportsWebAuthn()
}
